import { lazy, Suspense } from 'react';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { AuthLoadingGate } from '@/components/auth/AuthLoadingGate';
import { TeamProvider } from '@/contexts/TeamContext';
import { GlobalAdminProvider } from '@/contexts/GlobalAdminContext';
import { TeamGuard } from '@/components/guards/TeamGuard';
import { OnboardingGuard } from '@/components/guards/OnboardingGuard';
import { AdminGuard } from '@/components/guards/AdminGuard';
import { GarageGuard } from '@/components/guards/GarageGuard';
import { DeactivatedTeamGuard } from '@/components/guards/DeactivatedTeamGuard';
import { RouteLoader } from '@/components/shared/RouteLoader';
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary';
import { useIdlePrefetch } from '@/hooks/useIdlePrefetch';
import { QuickCalculatorProvider } from '@/features/calculator';
import { PageTitleController } from '@/components/PageTitleController';
import '@/app/i18n/i18n';

// Eager load: Auth pages (small, needed immediately)
import LoginPage from './app/routes/login/LoginPage';
import SignUpPage from './app/routes/signup/SignUpPage';
import ResetPasswordPage from './app/routes/reset-password/ResetPasswordPage';
import NotFound from './pages/NotFound';
import { EntityDetailRedirect } from './components/shared/EntityDetailRedirect';

// Lazy load: All protected routes (loaded on demand)
const DashboardPage = lazy(() => import('./app/routes/dashboard/DashboardPage'));
const CustomersPage = lazy(() => import('./app/routes/customers/CustomersPage'));
const VehiclesPage = lazy(() => import('./app/routes/vehicles/VehiclesPage'));
const PurchaseInvoicesPage = lazy(
  () => import('./app/routes/purchaseInvoices/PurchaseInvoicesPage')
);
const SalesInvoicesPage = lazy(() => import('./app/routes/salesInvoices/SalesInvoicesPage'));
const PipelinePage = lazy(() => import('./app/routes/pipeline/PipelinePage'));
const SettingsRouteLayout = lazy(() => import('./components/settings/SettingsRouteLayout'));
const BrandingSettingsPage = lazy(() => import('./app/routes/settings/BrandingSettingsPage'));
const TemplatesSettingsPage = lazy(() => import('./app/routes/settings/TemplatesSettingsPage'));
const SecuritySettingsPage = lazy(() => import('./app/routes/settings/SecuritySettingsPage'));
const PreferencesSettingsPage = lazy(() => import('./app/routes/settings/PreferencesSettingsPage'));
const AccountingSettingsPage = lazy(() => import('./app/routes/settings/AccountingSettingsPage'));
const PaymentsSettingsPage = lazy(() => import('./app/routes/settings/PaymentsSettingsPage'));
const ImportSettingsLayout = lazy(() => import('./app/routes/settings/ImportSettingsLayout'));
const VehicleImportWizard = lazy(() =>
  import('./features/import/components/VehicleImportWizard').then((m) => ({
    default: m.VehicleImportWizard,
  }))
);
const CustomerImportWizard = lazy(() =>
  import('./features/import/components/CustomerImportWizard').then((m) => ({
    default: m.CustomerImportWizard,
  }))
);
const InvoiceImportWizard = lazy(() =>
  import('./features/invoiceImport/components/InvoiceImportWizard').then((m) => ({
    default: m.InvoiceImportWizard,
  }))
);
const InvoiceTermsRoutePage = lazy(() => import('./app/routes/settings/InvoiceTermsRoutePage'));
const SubscriptionSettingsPage = lazy(
  () => import('./app/routes/settings/SubscriptionSettingsPage')
);
const SupportPage = lazy(() => import('./app/routes/support/SupportPage'));
const ProfilePage = lazy(() => import('./app/routes/profile/ProfilePage'));
const AnalyticsPage = lazy(() => import('./app/routes/analytics/AnalyticsPage'));
const CalendarPage = lazy(() => import('./app/routes/calendar/CalendarPage'));
const GaragePage = lazy(() => import('./app/routes/garage'));
const InventoryPage = lazy(() => import('./app/routes/inventory'));
const TasksPage = lazy(() => import('./app/routes/tasks/TasksPage'));

// SmartAdd
const SmartAddPurchaseInvoiceReview = lazy(() =>
  import('./features/smartAdd/components/SmartAddPurchaseInvoiceReview').then((m) => ({
    default: m.SmartAddPurchaseInvoiceReview,
  }))
);

// Onboarding pages
const WelcomeOnboardingPage = lazy(() => import('./app/routes/onboarding/WelcomeOnboardingPage'));
const CreateTeamPage = lazy(() => import('./app/routes/onboarding/CreateTeamPage'));
const AcceptInvitePage = lazy(() => import('./app/routes/invite/AcceptInvitePage'));
const VerifyEmailPage = lazy(() => import('./app/routes/verify-email/VerifyEmailPage'));
const VerifyCompletePage = lazy(() => import('./app/routes/verify-complete/VerifyCompletePage'));
const ContinueSignupPage = lazy(() => import('./app/routes/signup/ContinueSignupPage'));

// Admin pages
const TeamManagementPage = lazy(() => import('./app/routes/admin/TeamManagementPage'));

// Team picker — safe landing page when activeTeamId is invalid/deactivated
const TeamPickerPage = lazy(() => import('./app/routes/team-picker/TeamPickerPage'));

// Team inactive — full-screen page for inactive teams (CONTRACT V2)
const TeamInactivePage = lazy(() => import('./app/routes/team-inactive/TeamInactivePage'));

// Configure React Query for offline support
// Freshness policy (see docs/UI_SYSTEM_CONTRACT_V1.md § Freshness Policy):
//   - Local post-mutation refresh: entity event bus + invalidateQueries (RULE-7)
//   - Cross-user baseline: global staleTime (30s) + refetchOnWindowFocus + refetchOnMount
//   - Real-time list listeners: follow-up (see docs/DATA_LOADING_AUDIT_V1.md Phase 1)
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // 30-second staleness window — balances cross-user freshness with Firestore read cost.
      // Hooks that need longer caching (e.g. selectors) override this per-query.
      staleTime: 30 * 1000,
      // Cache data for 10 minutes
      gcTime: 10 * 60 * 1000,
      // Always refetch when navigating to a page (shows cached data + fetches fresh)
      refetchOnMount: 'always',
      // Retry failed queries (useful for offline/online transitions)
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Disable refetch on window focus to reduce Firestore reads
      refetchOnWindowFocus: false,
      // Network mode: show cached data when offline
      networkMode: 'offlineFirst',
    },
    mutations: {
      // Retry mutations when coming back online
      retry: 1,
      // Network mode: queue mutations when offline
      networkMode: 'offlineFirst',
    },
  },
});

// Component that handles idle prefetching
function AppWithPrefetch({ children }: { children: React.ReactNode }) {
  useIdlePrefetch();
  return <>{children}</>;
}

/**
 * Layout for admin routes (NO TeamProvider).
 */
function AdminLayout() {
  return (
    <Suspense fallback={<RouteLoader />}>
      <Outlet />
    </Suspense>
  );
}

/**
 * Layout for main app routes. TeamProvider is provided by TeamProviderLayout above.
 */
function MainAppLayout() {
  return (
    <QuickCalculatorProvider>
      <AppWithPrefetch>
        <OnboardingGuard>
          <DeactivatedTeamGuard>
            <TeamGuard>
              <AppShell>
                <Suspense fallback={<RouteLoader />}>
                  <Outlet />
                </Suspense>
              </AppShell>
            </TeamGuard>
          </DeactivatedTeamGuard>
        </OnboardingGuard>
      </AppWithPrefetch>
    </QuickCalculatorProvider>
  );
}

/**
 * Single shared TeamProvider for /onboarding/welcome and all main app routes.
 * A single instance means activeTeamId, team, and onboardingJustCompleted all
 * survive the /onboarding/welcome → /dashboard route transition.
 */
function TeamProviderLayout() {
  return (
    <TeamProvider>
      <Outlet />
    </TeamProvider>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        {/* AuthLoadingGate ensures auth state is resolved before any routing decisions */}
        <AuthLoadingGate>
          {/* GlobalAdminProvider: single source of truth for platform admin status */}
          <GlobalAdminProvider>
            <PageTitleController />

            <Routes>
              {/* Public routes - no auth required */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignUpPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />

              <Route
                path="/invite/:inviteId"
                element={
                  <Suspense fallback={<RouteLoader />}>
                    <AcceptInvitePage />
                  </Suspense>
                }
              />

              {/* All authenticated routes */}
              <Route element={<ProtectedRoute />}>
                {/* Email verification route - requires auth */}
                <Route
                  path="/verify-email"
                  element={
                    <Suspense fallback={<RouteLoader />}>
                      <VerifyEmailPage />
                    </Suspense>
                  }
                />

                {/* Verification complete route - deterministic handler for email verification links */}
                <Route
                  path="/verify-complete"
                  element={
                    <Suspense fallback={<RouteLoader />}>
                      <VerifyCompletePage />
                    </Suspense>
                  }
                />

                {/* Continue signup route - for OAuth users without account */}
                <Route
                  path="/signup/continue"
                  element={
                    <Suspense fallback={<RouteLoader />}>
                      <ContinueSignupPage />
                    </Suspense>
                  }
                />

                {/* Onboarding routes - require auth but NOT team (NO TeamProvider) */}
                <Route
                  path="/onboarding/create-team"
                  element={
                    <RouteErrorBoundary routeName="Create Team" fallbackRoute="/login">
                      <Suspense fallback={<RouteLoader />}>
                        <CreateTeamPage />
                      </Suspense>
                    </RouteErrorBoundary>
                  }
                />
                <Route
                  path="/onboarding/complete-setup"
                  element={<Navigate to="/onboarding/welcome" replace />}
                />
                <Route
                  path="/onboarding/trial-pending"
                  element={<Navigate to="/dashboard" replace />}
                />
                {/* Team picker — requires auth only, NO TeamProvider, safe for deactivated/invalid teams */}
                <Route
                  path="/team-picker"
                  element={
                    <RouteErrorBoundary routeName="Team Picker" fallbackRoute="/login">
                      <Suspense fallback={<RouteLoader />}>
                        <TeamPickerPage />
                      </Suspense>
                    </RouteErrorBoundary>
                  }
                />

                {/* Team inactive — full-screen page for inactive teams (CONTRACT V2) */}
                <Route
                  path="/team-inactive"
                  element={
                    <RouteErrorBoundary routeName="Team Inactive" fallbackRoute="/login">
                      <Suspense fallback={<RouteLoader />}>
                        <TeamInactivePage />
                      </Suspense>
                    </RouteErrorBoundary>
                  }
                />

                {/* Admin routes - require platform admin, NO team, NO TeamProvider */}
                <Route
                  path="/admin"
                  element={
                    <AdminGuard>
                      <AdminLayout />
                    </AdminGuard>
                  }
                >
                  <Route index element={<Navigate to="teams" replace />} />
                  <Route path="teams" element={<TeamManagementPage />} />
                </Route>

                {/* Single TeamProvider wrapping /onboarding/welcome + all main app routes.
                    State persists across the onboarding → dashboard route transition. */}
                <Route element={<TeamProviderLayout />}>
                  {/* /onboarding/welcome: needs TeamProvider but NOT OnboardingGuard (to avoid loops) */}
                  <Route
                    path="/onboarding/welcome"
                    element={
                      <RouteErrorBoundary routeName="Onboarding" fallbackRoute="/login">
                        <Suspense fallback={<RouteLoader />}>
                          <WelcomeOnboardingPage />
                        </Suspense>
                      </RouteErrorBoundary>
                    }
                  />

                  {/* Main app routes - require team membership */}
                  <Route element={<MainAppLayout />}>
                    <Route index element={<Navigate to="/dashboard" replace />} />

                    <Route
                      path="/dashboard"
                      element={
                        <RouteErrorBoundary routeName="Dashboard" fallbackRoute="/dashboard">
                          <DashboardPage />
                        </RouteErrorBoundary>
                      }
                    />

                    <Route
                      path="/vehicles"
                      element={
                        <RouteErrorBoundary routeName="Vehicles" fallbackRoute="/dashboard">
                          <VehiclesPage />
                        </RouteErrorBoundary>
                      }
                    />
                    <Route
                      path="/vehicles/:id"
                      element={<EntityDetailRedirect listRoute="/vehicles" />}
                    />

                    <Route
                      path="/customers"
                      element={
                        <RouteErrorBoundary routeName="Customers" fallbackRoute="/dashboard">
                          <CustomersPage />
                        </RouteErrorBoundary>
                      }
                    />
                    <Route
                      path="/customers/:id"
                      element={<EntityDetailRedirect listRoute="/customers" />}
                    />

                    <Route
                      path="/purchase-invoices"
                      element={
                        <RouteErrorBoundary
                          routeName="Purchase Invoices"
                          fallbackRoute="/dashboard"
                        >
                          <PurchaseInvoicesPage />
                        </RouteErrorBoundary>
                      }
                    />
                    <Route
                      path="/purchase-invoices/smart-add/:uploadId"
                      element={
                        <RouteErrorBoundary
                          routeName="Smart Add Review"
                          fallbackRoute="/purchase-invoices"
                        >
                          <SmartAddPurchaseInvoiceReview />
                        </RouteErrorBoundary>
                      }
                    />
                    <Route
                      path="/purchase-invoices/:id"
                      element={<EntityDetailRedirect listRoute="/purchase-invoices" />}
                    />

                    <Route
                      path="/sales-invoices"
                      element={
                        <RouteErrorBoundary routeName="Sales Invoices" fallbackRoute="/dashboard">
                          <SalesInvoicesPage />
                        </RouteErrorBoundary>
                      }
                    />
                    <Route
                      path="/sales-invoices/:id"
                      element={<EntityDetailRedirect listRoute="/sales-invoices" />}
                    />

                    <Route
                      path="/pipeline"
                      element={
                        <RouteErrorBoundary routeName="Pipeline" fallbackRoute="/dashboard">
                          <PipelinePage />
                        </RouteErrorBoundary>
                      }
                    />

                    <Route
                      path="/analytics"
                      element={
                        <RouteErrorBoundary routeName="Analytics" fallbackRoute="/dashboard">
                          <AnalyticsPage />
                        </RouteErrorBoundary>
                      }
                    />

                    <Route
                      path="/calendar"
                      element={
                        <RouteErrorBoundary routeName="Calendar" fallbackRoute="/dashboard">
                          <CalendarPage />
                        </RouteErrorBoundary>
                      }
                    />

                    <Route
                      path="/garage"
                      element={
                        <GarageGuard>
                          <RouteErrorBoundary routeName="Garage" fallbackRoute="/dashboard">
                            <GaragePage />
                          </RouteErrorBoundary>
                        </GarageGuard>
                      }
                    />

                    <Route
                      path="/inventory"
                      element={
                        <RouteErrorBoundary routeName="Inventory" fallbackRoute="/dashboard">
                          <InventoryPage />
                        </RouteErrorBoundary>
                      }
                    />

                    <Route
                      path="/tasks"
                      element={
                        <RouteErrorBoundary routeName="Tasks" fallbackRoute="/dashboard">
                          <TasksPage />
                        </RouteErrorBoundary>
                      }
                    />

                    <Route
                      path="/settings/*"
                      element={
                        <RouteErrorBoundary routeName="Settings" fallbackRoute="/dashboard">
                          <SettingsRouteLayout />
                        </RouteErrorBoundary>
                      }
                    >
                      <Route path="branding" element={<BrandingSettingsPage />} />
                      <Route path="templates" element={<TemplatesSettingsPage />} />
                      <Route path="security" element={<SecuritySettingsPage />} />
                      <Route path="preferences" element={<PreferencesSettingsPage />} />
                      <Route path="accounting" element={<AccountingSettingsPage />} />
                      <Route path="payments" element={<PaymentsSettingsPage />} />
                      <Route path="import" element={<ImportSettingsLayout />}>
                        <Route index element={<Navigate to="vehicles" replace />} />
                        <Route path="vehicles" element={<VehicleImportWizard />} />
                        <Route path="customers" element={<CustomerImportWizard />} />
                        <Route path="invoices" element={<InvoiceImportWizard />} />
                      </Route>
                      <Route path="invoiceTerms" element={<InvoiceTermsRoutePage />} />
                      <Route path="subscription" element={<SubscriptionSettingsPage />} />
                    </Route>

                    <Route
                      path="/settings/profile"
                      element={
                        <RouteErrorBoundary routeName="Profile" fallbackRoute="/settings">
                          <ProfilePage />
                        </RouteErrorBoundary>
                      }
                    />

                    <Route
                      path="/support"
                      element={
                        <RouteErrorBoundary routeName="Support" fallbackRoute="/dashboard">
                          <SupportPage />
                        </RouteErrorBoundary>
                      }
                    />

                    <Route
                      path="/profile"
                      element={
                        <RouteErrorBoundary routeName="Profile" fallbackRoute="/dashboard">
                          <ProfilePage />
                        </RouteErrorBoundary>
                      }
                    />

                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                  </Route>
                </Route>
              </Route>

              {/* Fallback for truly unknown public routes */}
              <Route path="*" element={<NotFound />} />
            </Routes>

            {/* Sonner toaster rendered LAST to ensure it's on top of all dialogs */}
            <Sonner />
          </GlobalAdminProvider>
        </AuthLoadingGate>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
